import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Delete } from '@mui/icons-material';
import {
  createShipmentCarrier,
  deleteShipmentCarrier,
  fetchShipmentCarriers,
} from '../../services/shipment';

export default function CarrierManagement() {
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadCarriers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchShipmentCarriers();
      setCarriers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load carriers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCarriers();
  }, [loadCarriers]);

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const created = await createShipmentCarrier({ name: trimmedName });
      setCarriers((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setName('');
      setSuccess('Carrier added successfully');
    } catch (err) {
      setError(
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        'Failed to add carrier'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (carrier) => {
    setDeletingId(carrier.id);
    setError('');
    setSuccess('');
    try {
      await deleteShipmentCarrier(carrier.id);
      setCarriers((prev) => prev.filter((item) => item.id !== carrier.id));
      setSuccess(`Removed carrier "${carrier.name}"`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to remove carrier');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Carrier Management</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/admin/shipments')}>
          Back to Shipments
        </Button>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          {!!error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!!success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Carrier Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreate();
                }
              }}
            />
            <Button
              variant="contained"
              disabled={saving || !name.trim()}
              onClick={handleCreate}
            >
              {saving ? 'Adding...' : 'Add Carrier'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
          ) : carriers.length === 0 ? (
            <Typography color="text.secondary">No carriers available yet.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {carriers.map((carrier) => (
                <Stack
                  key={carrier.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 2, py: 1.5 }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{carrier.name}</Typography>
                  </Box>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(carrier)}
                    disabled={deletingId === carrier.id}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
